import { Package, Plus, Wrench } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { CartItem, ItemVariant, POSCustomField, POSItem } from '@/types';
import { getItemPrice } from '@/utils/posItemPricing';
import { resolveFileUrl } from '@/utils/fileUrl';
import { formatMoney } from '@/utils/currency';
import { previewItemPromo, type PromotionCampaign } from '@/utils/promotions';
import { PosPromoPrice, PosSaleBadge } from '@/components/pos/PosSaleBadge';

function getCartQtyForItem(
  items: CartItem[],
  itemName: string,
  variant?: string,
) {
  return items.reduce((sum, line) => {
    if (line.item_type !== 'Product' || line.item !== itemName) return sum;
    if (variant !== undefined && (line.variant || '') !== variant) return sum;
    return sum + line.quantity;
  }, 0);
}

function stockBadgeClass(qty: number) {
  if (qty <= 0) return 'bg-red-50 text-red-700';
  if (qty <= 5) return 'bg-amber-50 text-amber-700';
  return 'bg-emerald-50 text-emerald-700';
}

function getVariantAvailable(v: ItemVariant, item: POSItem, cartItems: CartItem[]) {
  const stock = v.stock_quantity ?? 0;
  const inCart = getCartQtyForItem(cartItems, item.name, v.name);
  return { stock, available: stock - inCart };
}

function canAddProductQty(
  item: POSItem,
  cartItems: CartItem[],
  allowOos: boolean,
  addQty = 1,
  variant?: ItemVariant,
) {
  if (item.item_type !== 'Product' || allowOos) return true;
  const stock = variant ? (variant.stock_quantity ?? 0) : (item.stock_quantity ?? 0);
  const inCart = getCartQtyForItem(cartItems, item.name, variant?.name);
  return inCart + addQty <= stock;
}

interface PosDetailCardViewProps {
  items: POSItem[];
  cartItems: CartItem[];
  allowOos: boolean;
  posCustomFields: POSCustomField[];
  customerType: string;
  promotions?: PromotionCampaign[];
  onAddItem: (item: POSItem, variant?: ItemVariant) => void;
  onOpenVariantModal: (itemName: string) => void;
}

export default function PosDetailCardView({
  items,
  cartItems,
  allowOos,
  posCustomFields,
  customerType,
  promotions = [],
  onAddItem,
  onOpenVariantModal,
}: PosDetailCardViewProps) {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
      {items.map((item) => {
        const outOfStock = item.item_type === 'Product' && !allowOos && !item.has_variants && (item.stock_quantity ?? 0) <= 0;
        const cannotAddMore = item.item_type === 'Product' && !allowOos && !item.has_variants && !canAddProductQty(item, cartItems, allowOos);
        const itemPrice = getItemPrice(item, customerType);
        const promo = previewItemPromo(item, customerType, promotions);

        return (
          <div key={item.name} className="card p-4 hover:shadow-md transition-shadow relative overflow-visible">
            {promo && (
              <div className="absolute top-2 right-2 z-20">
                <PosSaleBadge preview={promo} />
              </div>
            )}
            <div className="flex items-start gap-3 mb-3">
              {item.image ? (
                <div className="relative -mt-6 -ml-6 w-20 h-20 bg-white rounded-xl border border-gray-200/80 shadow-md overflow-hidden flex-shrink-0 z-10">
                  <img
                    src={resolveFileUrl(item.image)}
                    alt={item.item_name}
                    className="w-full h-full object-cover"
                  />
                </div>
              ) : (
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${item.item_type === 'Product' ? 'bg-blue-100' : 'bg-purple-100'
                  }`}>
                  {item.item_type === 'Product'
                    ? <Package className="w-5 h-5 text-blue-600" />
                    : <Wrench className="w-5 h-5 text-purple-600" />
                  }
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-semibold text-gray-900 text-sm truncate">{item.item_name}</h3>
                  {item.item_type === 'Product' && (
                    <span
                      className={`shrink-0 text-xs font-semibold px-2 py-0.5 rounded-full ${stockBadgeClass(item.stock_quantity ?? 0)}`}
                      title={item.has_variants ? t('pos.totalStock') : t('pos.availableStock')}
                    >
                      {(item.stock_quantity ?? 0).toLocaleString()} {item.unit_of_measurement || 'pcs'}
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-400 mt-0.5">
                  {item.category}
                  {!item.has_variants && (
                    <> &middot; <PosPromoPrice price={itemPrice} preview={promo} /></>
                  )}
                </p>
                {posCustomFields.some((field) => item.custom_fields_data?.[field.fieldname]) && (
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {posCustomFields.map((field) => {
                      const value = item.custom_fields_data?.[field.fieldname];
                      return value ? (
                        <span key={field.fieldname} className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
                          {field.label}: {value}
                        </span>
                      ) : null;
                    })}
                  </div>
                )}
              </div>
            </div>

            {item.has_variants && item.variants && item.variants.length > 0 ? (
              <div className="space-y-2">
                {(item.variants.length > 3 ? item.variants.slice(0, 3) : item.variants).map((v: ItemVariant) => {
                  const { stock } = getVariantAvailable(v, item, cartItems);
                  const variantOos = item.item_type === 'Product' && !allowOos && stock <= 0;
                  const variantMaxed = item.item_type === 'Product' && !allowOos && !canAddProductQty(item, cartItems, allowOos, 1, v);
                  const variantPrice = getItemPrice(item, customerType, v);

                  return (
                    <button
                      key={v.name}
                      onClick={() => onAddItem(item, v)}
                      disabled={variantOos || variantMaxed}
                      className={`w-full flex items-center justify-between px-3 py-2 border rounded-lg text-sm transition-colors group ${variantOos || variantMaxed
                        ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
                        : 'bg-gray-50 hover:bg-primary-50 border-gray-100 hover:border-primary-200'
                        }`}
                    >
                      <span className="flex items-center gap-2 min-w-0">
                        <Plus className={`w-3.5 h-3.5 shrink-0 ${variantOos || variantMaxed ? 'text-gray-400' : 'text-primary-600'}`} />
                        <span className="text-gray-700 truncate">{v.variant_name}</span>
                      </span>
                      <span className="flex items-center gap-2 shrink-0 ml-2">
                        <span className="text-gray-500 text-xs">{formatMoney(variantPrice)}/{v.unit_of_measurement}</span>
                        {item.item_type === 'Product' && (
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${stockBadgeClass(stock)}`}>
                            {stock.toLocaleString()} {v.unit_of_measurement || item.unit_of_measurement || 'pcs'}
                          </span>
                        )}
                      </span>
                    </button>
                  );
                })}
                {item.variants.length > 3 && (
                  <button
                    type="button"
                    onClick={() => onOpenVariantModal(item.name)}
                    className="w-full px-3 py-2 border border-dashed border-gray-300 rounded-lg text-sm font-medium text-primary-700 hover:bg-primary-50 hover:border-primary-300 transition-colors bg-primary-50"
                  >
                    {t('pos.showAllVariants', { count: item.variants.length })}
                  </button>
                )}
              </div>
            ) : item.has_variants ? (
              <p className="text-xs text-center text-gray-400 py-2">{t('pos.itemOutOfStock')}</p>
            ) : (
              <button
                onClick={() => onAddItem(item)}
                disabled={outOfStock || cannotAddMore}
                className={`w-full flex items-center justify-center gap-2 px-3 py-2 border rounded-lg text-sm font-medium transition-colors ${outOfStock || cannotAddMore
                  ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-primary-50 hover:bg-primary-100 border-primary-100 text-primary-700'
                  }`}
              >
                <Plus className="w-4 h-4" />
                {outOfStock
                  ? t('pos.outOfStockShort')
                  : cannotAddMore
                    ? t('pos.maxStockInCart')
                    : t('pos.addWithPrice', { price: formatMoney(promo?.promoPrice ?? itemPrice) })}
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
